import { addToCart } from "./cart.js";

function replaceElement(newElement, currentElement) {
  if (currentElement && newElement) {
    currentElement.replaceWith(newElement);
    return true;
  }
  return false;
}

function createStickySentinel(element) {
  const sentinel = document.createElement("div");
  sentinel.className = "product__form-sticky-sentinel";
  element.parentNode.insertBefore(sentinel, element);
  return sentinel;
}

function initStickyElement(element) {
  element.setAttribute("data-sticky-initialized", "true");

  const sentinel = createStickySentinel(element);
  const footer = document.querySelector(".footer");

  const isMobile = () => window.innerWidth <= 768;

  const FOOTER_STOP_BUFFER_PX = 16;

  const getTemp = () => document.querySelector(".js-atc-temp");

  const ensureTemp = () => {
    let atcTemp = getTemp();
    if (atcTemp) return atcTemp;

    atcTemp = document.createElement("div");
    atcTemp.classList.add("js-atc-temp", "atc-temp");
    atcTemp.style.height = `${element.getBoundingClientRect().height}px`;
    element.after(atcTemp);
    return atcTemp;
  };

  const removeTemp = () => {
    const atcTemp = getTemp();
    if (atcTemp) atcTemp.remove();
  };

  const isFooterTooClose = () => {
    if (!footer) return false;
    if (!isMobile()) return false;

    const footerRect = footer.getBoundingClientRect();
    const stickyHeight = element.getBoundingClientRect().height;

    return footerRect.top <= window.innerHeight - stickyHeight - FOOTER_STOP_BUFFER_PX;
  };

  const setSticky = (shouldStick) => {
    if (!isMobile()) {
      element.classList.remove("is-sticky");
      removeTemp();
      return;
    }

    if (shouldStick && isFooterTooClose()) shouldStick = false;

    const isCurrentlySticky = element.classList.contains("is-sticky");
    if (shouldStick === isCurrentlySticky) return;

    if (shouldStick) {
      const temp = ensureTemp();
      temp.style.height = `${element.getBoundingClientRect().height}px`;

      temp.getBoundingClientRect();

      element.classList.add("is-sticky");
    } else {
      element.classList.remove("is-sticky");

      element.getBoundingClientRect();

      removeTemp();
    }
  };

  const computeShouldStickNow = () => {
    if (!isMobile()) return false;
    if (isFooterTooClose()) return false;

    const r = sentinel.getBoundingClientRect();
    const inView = r.bottom > 0 && r.top < window.innerHeight;
    return !inView;
  };

  setSticky(computeShouldStickNow());

  const observer = new IntersectionObserver(
    (entries) => {
      if (!isMobile()) {
        setSticky(false);
        return;
      }

      const sentEntry = entries.find((e) => e.target === sentinel);
      if (!sentEntry) return;

      setSticky(!sentEntry.isIntersecting);
    },
    {
      root: null,
      rootMargin: "0px",
      threshold: 0,
    },
  );

  observer.observe(sentinel);

  let ticking = false;
  const onScroll = () => {
    if (!isMobile()) return;
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(() => {
      ticking = false;

      const footerClose = isFooterTooClose();
      const isSticky = element.classList.contains("is-sticky");

      if (footerClose) {
        if (isSticky) setSticky(false);
        return;
      }

      const r = sentinel.getBoundingClientRect();
      const sentinelInView = r.bottom > 0 && r.top < window.innerHeight;

      if (!sentinelInView) {
        if (!isSticky) setSticky(true);
      } else {
        if (isSticky) setSticky(false);
      }
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });

  const syncTempHeight = () => {
    const temp = getTemp();
    if (!temp) return;
    temp.style.height = `${element.getBoundingClientRect().height}px`;
  };

  const onResize = () => {
    setSticky(computeShouldStickNow());
    syncTempHeight();
  };

  window.addEventListener("resize", onResize, { passive: true });
  window.addEventListener("load", onResize);

  element._stickyObserver = observer;
  element._stickySentinel = sentinel;

  element._stickyOnScroll = onScroll;
  element._stickyOnResize = onResize;
}

function cleanupStickyElement(element) {
  if (!element) return;

  if (element._stickyObserver) {
    element._stickyObserver.disconnect();
    element._stickyObserver = null;
  }

  if (element._stickySentinel) {
    element._stickySentinel.remove();
    element._stickySentinel = null;
  }

  if (element._stickyOnScroll) {
    window.removeEventListener("scroll", element._stickyOnScroll);
    element._stickyOnScroll = null;
  }

  if (element._stickyOnResize) {
    window.removeEventListener("resize", element._stickyOnResize);
    window.removeEventListener("load", element._stickyOnResize);
    element._stickyOnResize = null;
  }

  const temp = document.querySelector(".js-atc-temp");
  if (temp) temp.remove();

  element.classList.remove("is-sticky");
  element.removeAttribute("data-sticky-initialized");
}

function initStickyElements(root = document) {
  if (window.innerWidth > 768) return;

  const stickyElements = root.querySelectorAll("[data-sticky]:not([data-sticky-initialized])");
  if (!stickyElements.length) return;

  stickyElements.forEach(initStickyElement);
  return Array.from(stickyElements);
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleStickyResize = this.handleStickyResize.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.rechargeObserver = null;
  }

  pruneStickyElements() {
    if (!this._stickyElements?.length) {
      this._stickyElements = [];
      return;
    }

    this._stickyElements = this._stickyElements.filter((el) => {
      if (!el) return false;
      if (!el.isConnected) {
        cleanupStickyElement(el);
        return false;
      }
      return true;
    });
  }

  addStickyElements(elements) {
    if (!elements?.length) return;
    this._stickyElements ||= [];
    this.pruneStickyElements();

    const seen = new Set(this._stickyElements);
    elements.forEach((el) => {
      if (el && !seen.has(el)) {
        seen.add(el);
        this._stickyElements.push(el);
      }
    });
  }

  connectedCallback() {
    this.form = this.querySelector("form[data-product-form]");
    if (!this.form) return;

    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.form.addEventListener("submit", this.handleSubmit);

    this._stickyElements = [];
    const addedSticky = initStickyElements(this);
    this.addStickyElements(addedSticky);

    window.addEventListener("resize", this.handleStickyResize, { passive: true });
    this.handleStickyResize();

    document.addEventListener("cart:updated", this.handleCartUpdate);

    if (!this.hasAttribute("data-product")) return;
    this.initRechargeComparePrice();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener("submit", this.handleSubmit);
    }

    window.removeEventListener("resize", this.handleStickyResize);
    document.removeEventListener("cart:updated", this.handleCartUpdate);

    if (this._stickyElements?.length) {
      this._stickyElements.forEach(cleanupStickyElement);
      this._stickyElements = [];
    }

    if (this.rechargeObserver) {
      this.rechargeObserver.disconnect();
      this.rechargeObserver = null;
    }
  }

  handleStickyResize() {
    if (!this.isConnected) return;

    this.pruneStickyElements();

    if (window.innerWidth > 768) {
      this._stickyElements?.forEach(cleanupStickyElement);
      this._stickyElements = [];
      return;
    }

    const addedSticky = initStickyElements(this);
    this.addStickyElements(addedSticky);
  }

  async handleCartUpdate(event) {
    if (!this.form) return;

    const { variantIds } = event.detail || {};
    if (!Array.isArray(variantIds) || variantIds.length === 0) return;

    const variantInput = this.form.querySelector('input[name="id"]');
    const currentActiveVariantId = Number(variantInput?.value || this.form.getAttribute("data-variant-id"));

    if (!currentActiveVariantId || currentActiveVariantId <= 0) return;

    if (variantIds.map(Number).includes(currentActiveVariantId)) {
      await this.refreshForm();
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.form || !this.submitButton) return;

    const formData = new FormData(this.form);

    this.submitButton.disabled = true;
    this.submitButton.classList.add("product-atc__button--loading");

    try {
      await addToCart(formData);

      await this.refreshForm();
    } catch (error) {
      console.error("Failed to add to cart:", error);
      alert("Failed to add item to cart. Please try again.");
    } finally {
      if (this.submitButton && this.submitButton.isConnected) {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove("product-atc__button--loading");
      }
    }
  }

  async refreshForm() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("section_id", "product");
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error("Failed to fetch product section");

      const html = await response.text();
      if (!html) return;

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const newProductForm = tempDiv.querySelector("product-form");
      if (!newProductForm) return;

      replaceElement(newProductForm.querySelector("quantity-selector"), this.querySelector("quantity-selector"));

      const newSubmitButton = newProductForm.querySelector('button[type="submit"]');
      const currentSubmitButton = this.form?.querySelector('button[type="submit"]');
      if (replaceElement(newSubmitButton, currentSubmitButton) && newSubmitButton) {
        newSubmitButton.classList.remove("product-atc__button--loading");
        this.submitButton = newSubmitButton;
      }

      const newVariantInput = newProductForm.querySelector('form[data-product-form] input[name="id"]');
      const currentVariantInput = this.form?.querySelector('input[name="id"]');
      if (currentVariantInput && newVariantInput) {
        currentVariantInput.value = newVariantInput.value;
      }

      const newForm = newProductForm.querySelector("form[data-product-form]");
      if (newForm && this.form) {
        ["data-variant-id", "data-variant-available", "data-variant-inventory-quantity"].forEach((attr) => {
          this.form.setAttribute(attr, newForm.getAttribute(attr) || "");
        });
      }

      this.pruneStickyElements();
      const addedSticky = initStickyElements(this);
      this.addStickyElements(addedSticky);
    } catch (error) {
      console.error("Failed to refresh form:", error);
    }
  }

  initRechargeComparePrice = () => {
    const data = this.getAttribute("data-product");
    if (!data) return;

    let productData = null;
    try {
      productData = JSON.parse(data);
    } catch (e) {
      return;
    }
    if (!productData) return;

    const options = {
      childList: true,
      subtree: true,
    };

    if (this.rechargeObserver) {
      this.rechargeObserver.disconnect();
    }

    this.rechargeObserver = new MutationObserver((mutations) => {
      if (mutations.length) {
        mutations.forEach((mutation) => {
          const target = mutation.target;

          if (target && target.hasAttribute("data-price-onetime")) {
            this.updateCompareAtPrice(target, productData.compare_at_price, productData.compare_at_price_format, productData.price);
          }

          if (target && target.hasAttribute("data-price-subsave")) {
            const sellingPlanDropdown = this.querySelector("[data-plans-dropdown]");
            if (sellingPlanDropdown) {
              const planId = Number(sellingPlanDropdown.value);
              const sellingPlan = productData.selling_plan_allocations.find((selling_plan) => selling_plan.selling_plan_id === planId);
              if (sellingPlan) {
                this.updateCompareAtPrice(target, sellingPlan.compare_at_price, sellingPlan.compare_at_price_format, sellingPlan.price);
              }
            }
          }
        });
      }
    });

    this.rechargeObserver.observe(this, options);
  };

  updateCompareAtPrice = (target, compareAtPrice, compareAtPriceFormat, price) => {
    if (this.getAttribute("data-recharge-initialized") !== "true") {
      this.setAttribute("data-recharge-initialized", "true");
    }
    const existing = target.querySelector("s");
    if (compareAtPrice > price) {
      if (existing) {
        existing.textContent = compareAtPriceFormat;
      } else {
        const comparePriceEl = document.createElement("s");
        comparePriceEl.textContent = compareAtPriceFormat;
        target.prepend(comparePriceEl);
      }
      return;
    }
    if (existing) existing.remove();
  };
}

if (!customElements.get("product-form")) {
  customElements.define("product-form", ProductForm);
}
